import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { DispatchDateService } from './dispatch-date.service';
import { Session } from '../interfaces';
import { EntityRoute, Entity, RouteFilters, HolidayException, HolidayExceptionRequest, ResponseHolidayException } from '../models';

@Injectable({
  providedIn: 'root'
})
export class EntityRouteService {

  constructor(
    private apiService: ApiService,
    private dispatchDateService: DispatchDateService
  ) {}

  readById(id: string): Observable<EntityRoute[]> {
    return this.apiService
      .get(this.apiService.entityRoutes + '/' + id)
      .pipe(map((response: { routes: EntityRoute[] }) => response.routes));
  }

  readByType(entityType: string): Observable<EntityRoute[]> {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (
      session &&
      session.entitiesRoutes &&
      session.entitiesRoutes[entityType]
    ) {
      return of(session.entitiesRoutes[entityType]);
    } else {
      return this.apiService
        .get(
          this.apiService.entityRoutesByType.replace(
            '{{entityType}}',
            entityType
          )
        )
        .pipe(
          map((entitiesRoutes: EntityRoute[]) => {
            if (!session.entitiesRoutes) {
              session.entitiesRoutes = {};
            }
            session.entitiesRoutes[entityType] = entitiesRoutes;
            this.setSession(session);
            return entitiesRoutes;
          })
        );
    }
  }

  getRoutes(routeFilters: RouteFilters): Observable<EntityRoute[]> {
    return this.apiService.post(this.apiService.routes, routeFilters);
  }

  update(data: any): Observable<EntityRoute> {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    return this.apiService.put(this.apiService.entityRoutes, data).pipe(
      map(() => {
        const entityRoute: EntityRoute = data;
        const configurationIndex = session.entitiesRoutes[
          entityRoute.origin.entityType
        ].findIndex((o, i) => o.id.toString() === entityRoute.id.toString());
        session.entitiesRoutes[entityRoute.origin.entityType][
          configurationIndex
        ] = entityRoute;
        this.setSession(session);
        return entityRoute;
      })
    );
  }

  readByEntity(entityCode: string): Observable<Entity[]> {
    return (
      this.apiService
        .get(
          this.apiService.entityRoutesByEntity.replace(
            '{{entityCode}}',
            entityCode
          )
        )
        .pipe(map((response: { destinies: Entity[] }) => response.destinies))
    );
  }

  create(data: any): Observable<EntityRoute[]> {
    return this.apiService.post(this.apiService.entityRoutes, data).pipe(
      map((response: { routes: EntityRoute[] }) => response.routes),
      map((entitiesRoutes: EntityRoute[]) => {
        const session: Session = JSON.parse(
          localStorage.getItem(environment.sessionAuth)
        );
        if (!session.entitiesRoutes) {
          session.entitiesRoutes = {};
        }
        session.entitiesRoutes.form = entitiesRoutes;
        delete session.entitiesRoutes[data.origin.entityType];
        this.setSession(session);
        return entitiesRoutes;
      })
    );
  }

  configure(data: any): Observable<any> {
    return this.apiService
      .post(this.apiService.entityRoutesConfigure, data)
      .pipe(
        map(response => {
          this.dispatchDateService.removeSession();
          return response;
        })
      );
  }

  getForm(): EntityRoute[] {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    return session.entitiesRoutes.form || [];
  }

  setSession(session: Session) {
    localStorage.setItem(environment.sessionAuth, JSON.stringify(session));
  }

  removeSession(entityType: string) {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session.entitiesRoutes && session.entitiesRoutes[entityType]) {
      delete session.entitiesRoutes[entityType];
      this.setSession(session);
    }
  }

  saveHolidayException(request: HolidayExceptionRequest): Observable<ResponseHolidayException> {
    return this.apiService.put(this.apiService.holidayExceptions, request).pipe();
  }

  getHolidayExceptions(request): Observable<HolidayException> {
    return this.apiService.get(this.apiService.holidayExceptions + '?' + request)
      .pipe();
  }
}
